import { useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';

import theme from '../../assets/theme';
import SlateRichTextEditor, { RichTextEditorType } from './SlateRichTextEditor';
import { SlideElement, SlideElementTextType } from './slide.types';

const SlideElementTextEditor = ({
  element,
  onTextElementChange,
  content,
  richTextEditor,
  setSelectedElements,
  autoFocus = false,
}: {
  index: number;
  element: SlideElementTextType;
  setSelectedElements: (element: SlideElement | null) => void;

  onTextElementChange: (
    newText: string,
    localElement: SlideElement,
    containerHeight: number,
  ) => void;
  richTextEditor: RichTextEditorType;
  content: string;
  autoFocus?: boolean;
}) => {
  const textElementHeightRef = useRef<HTMLDivElement>(null);
  const scale = useMemo(() => element.scale || 1, [element.scale]);
  const width = useMemo(() => element.width / scale, [element.width, scale]);
  const onChange = useCallback(
    (value: string) => {
      const containerHeight = textElementHeightRef.current?.clientHeight || 0;
      onTextElementChange(value, element, containerHeight * scale);
    },
    [element, onTextElementChange, scale],
  );
  const disabledButtons = useMemo(
    () => (element.lockedFromDeletion ? ['bullets', 'numbers'] : []),
    [element.lockedFromDeletion],
  );

  if (!content) return null;

  return (
    <TextElementHeightContainer
      ref={textElementHeightRef}
      $scale={scale}
      $width={width}
      $opacity={element.opacity !== undefined ? element.opacity : 1}
    >
      <SlateRichTextEditor
        onChange={onChange}
        initContent={content}
        richTextEditor={richTextEditor}
        setSelectedElements={setSelectedElements}
        disabledButtons={disabledButtons}
        disabledKeys={element.lockedFromDeletion ? ['Enter'] : []}
        autoFocus={autoFocus}
      />
    </TextElementHeightContainer>
  );
};

const TextElementHeightContainer = styled.div<{
  $scale: number;
  $width: number;
  $opacity: number;
}>`
  outline: 2px solid ${theme.colors.orange};
  transform: scale(${({ $scale }) => $scale || 1}, ${({ $scale }) => $scale || 1});
  width: ${({ $width }) => $width}px;
  opacity: ${({ $opacity }) => $opacity};
  transform-origin: top left;
`;
export default SlideElementTextEditor;
